import React, { useEffect, useState } from 'react';
import {
  Service,
  AddOn,
  QuestionnaireTemplate,
  PaginatedResponse,
  QuestionnaireConfig
} from '@/types';
import { Plus, Edit2, Trash2, CheckSquare, Square, FileText, X, Loader2 } from 'lucide-react';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { createId } from '@/lib/ids';
import { Input } from '@shared/components/ui/Input';
import { Select } from '@shared/components/ui/Select';
import { TextArea } from '@shared/components/ui/TextArea';
import { api } from '@/services/api';
import { Pagination } from '@shared/components/common/Pagination';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface ServicesTabProps {
  onAddService: (service: Service) => void;
  onUpdateService: (service: Service) => void;
  onDeleteService: (id: string) => void;
  onAddAddOn: (addOn: AddOn) => void;
  onUpdateAddOn: (addOn: AddOn) => void;
  onDeleteAddOn: (id: string) => void;
  canEdit: boolean;
}

const OPTIONS_PAGE_SIZE = 100;
const MAX_OPTIONS_PAGES = 50;
type QuestionnaireSendTimeUnit = QuestionnaireConfig['sendTimeUnit'];
const QUESTIONNAIRE_TIME_UNITS: Array<{ value: QuestionnaireSendTimeUnit; label: string }> = [
  { value: 'hours', label: 'Hours Prior' },
  { value: 'days', label: 'Days Prior' }
];

const isQuestionnaireSendTimeUnit = (value: string): value is QuestionnaireSendTimeUnit => {
  return value === 'hours' || value === 'days';
};

async function loadAllPages<T>(
  fetchPage: (params: Record<string, string | number | undefined>) => Promise<PaginatedResponse<T>>
): Promise<T[]> {
  const allItems: T[] = [];
  let page = 1;
  let totalCount = Number.POSITIVE_INFINITY;

  while (page <= MAX_OPTIONS_PAGES && allItems.length < totalCount) {
    const data = await fetchPage({ page, pageSize: OPTIONS_PAGE_SIZE });
    const items = data.items || [];
    totalCount = Number(data.totalCount || 0);
    if (!items.length) break;
    allItems.push(...items);
    page += 1;
  }

  return allItems;
}

export const ServicesTab: React.FC<ServicesTabProps> = ({
  onAddService,
  onUpdateService,
  onDeleteService,
  onAddAddOn,
  onUpdateAddOn,
  onDeleteAddOn,
  canEdit
}) => {
  const [serviceSubTab, setServiceSubTab] = useState<'services' | 'addons'>('services');
  const [services, setServices] = useState<Service[]>([]);
  const [servicesPage, setServicesPage] = useState(1);
  const [servicesPageSize, setServicesPageSize] = useState(25);
  const [servicesTotalCount, setServicesTotalCount] = useState(0);
  const [servicesSearch, setServicesSearch] = useState('');
  const [servicesError, setServicesError] = useState('');
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [serviceOptions, setServiceOptions] = useState<Service[]>([]);
  const [isLoadingServiceOptions, setIsLoadingServiceOptions] = useState(false);

  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [addOnsPage, setAddOnsPage] = useState(1);
  const [addOnsPageSize, setAddOnsPageSize] = useState(25);
  const [addOnsTotalCount, setAddOnsTotalCount] = useState(0);
  const [addOnsSearch, setAddOnsSearch] = useState('');
  const [addOnsError, setAddOnsError] = useState('');
  const [isLoadingAddOns, setIsLoadingAddOns] = useState(false);

  const [questionnaireOptions, setQuestionnaireOptions] = useState<QuestionnaireTemplate[]>([]);
  const [isLoadingQuestionnaires, setIsLoadingQuestionnaires] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [editingAddOn, setEditingAddOn] = useState<AddOn | null>(null);

  useEffect(() => {
    setServicesPage(1);
  }, [servicesSearch]);

  useEffect(() => {
    setAddOnsPage(1);
  }, [addOnsSearch]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingServices(true);
    setServicesError('');
    api
      .listStaffServices({ page: servicesPage, pageSize: servicesPageSize, q: servicesSearch })
      .then((data) => {
        if (!isActive) return;
        setServices(data.items || []);
        setServicesTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setServices([]);
        setServicesTotalCount(0);
        setServicesError(normalizeErrorMessage(err, 'Failed to load services.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingServices(false);
      });

    return () => {
      isActive = false;
    };
  }, [servicesPage, servicesPageSize, servicesSearch]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingServiceOptions(true);
    const loadServiceOptions = async () => {
      try {
        const items = await loadAllPages<Service>((params) => api.listStaffServices(params));
        if (!isActive) return;
        setServiceOptions(items);
      } catch {
        if (!isActive) return;
        setServiceOptions([]);
      } finally {
        if (isActive) setIsLoadingServiceOptions(false);
      }
    };
    loadServiceOptions();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    setIsLoadingAddOns(true);
    setAddOnsError('');
    api
      .listStaffAddOns({ page: addOnsPage, pageSize: addOnsPageSize, q: addOnsSearch })
      .then((data) => {
        if (!isActive) return;
        setAddOns(data.items || []);
        setAddOnsTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setAddOns([]);
        setAddOnsTotalCount(0);
        setAddOnsError(normalizeErrorMessage(err, 'Failed to load add-ons.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingAddOns(false);
      });

    return () => {
      isActive = false;
    };
  }, [addOnsPage, addOnsPageSize, addOnsSearch]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingQuestionnaires(true);
    const loadQuestionnaireOptions = async () => {
      try {
        const items = await loadAllPages<QuestionnaireTemplate>((params) =>
          api.listStaffQuestionnaires(params)
        );
        if (!isActive) return;
        setQuestionnaireOptions(items);
      } catch {
        if (!isActive) return;
        setQuestionnaireOptions([]);
      } finally {
        if (isActive) setIsLoadingQuestionnaires(false);
      }
    };
    loadQuestionnaireOptions();

    return () => {
      isActive = false;
    };
  }, []);

  const refreshServices = async () => {
    setIsLoadingServices(true);
    setServicesError('');
    try {
      const data = await api.listStaffServices({
        page: servicesPage,
        pageSize: servicesPageSize,
        q: servicesSearch
      });
      setServices(data.items || []);
      setServicesTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setServices([]);
      setServicesTotalCount(0);
      setServicesError(normalizeErrorMessage(err, 'Failed to load services.'));
    } finally {
      setIsLoadingServices(false);
    }
  };

  const refreshAddOns = async () => {
    setIsLoadingAddOns(true);
    setAddOnsError('');
    try {
      const data = await api.listStaffAddOns({
        page: addOnsPage,
        pageSize: addOnsPageSize,
        q: addOnsSearch
      });
      setAddOns(data.items || []);
      setAddOnsTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setAddOns([]);
      setAddOnsTotalCount(0);
      setAddOnsError(normalizeErrorMessage(err, 'Failed to load add-ons.'));
    } finally {
      setIsLoadingAddOns(false);
    }
  };

  const handleUpdateQuestionnaire = (templateId: string) => {
    if (!editingService) return;

    if (!templateId) {
      setEditingService({
        ...editingService,
        questionnaireConfig: undefined
      });
      return;
    }

    // Preserve existing time settings if they exist
    const currentConfig = editingService.questionnaireConfig;
    setEditingService({
      ...editingService,
      questionnaireConfig: {
        templateId,
        sendTimeValue: currentConfig?.sendTimeValue || 24,
        sendTimeUnit: currentConfig?.sendTimeUnit || 'hours'
      }
    });
  };

  const handleUpdateAddOnQuestionnaire = (templateId: string) => {
    if (!editingAddOn) return;

    if (!templateId) {
      setEditingAddOn({
        ...editingAddOn,
        questionnaireConfig: undefined
      });
      return;
    }

    const currentConfig = editingAddOn.questionnaireConfig;
    setEditingAddOn({
      ...editingAddOn,
      questionnaireConfig: {
        templateId,
        sendTimeValue: currentConfig?.sendTimeValue || 24,
        sendTimeUnit: currentConfig?.sendTimeUnit || 'hours'
      }
    });
  };

  const toggleServiceCompatibility = (serviceId: string) => {
    if (!editingAddOn) return;
    const current = editingAddOn.compatibleServiceIds || [];
    if (current.includes(serviceId)) {
      setEditingAddOn({
        ...editingAddOn,
        compatibleServiceIds: current.filter((id) => id !== serviceId)
      });
    } else {
      setEditingAddOn({ ...editingAddOn, compatibleServiceIds: [...current, serviceId] });
    }
  };

  type TierPrices = NonNullable<Service['tierPrices']>;

  const applyTierPrice = (
    current: TierPrices | undefined,
    tier: keyof TierPrices,
    value: string
  ) => {
    const next = { ...(current || {}) } as TierPrices;
    if (value === '') {
      delete next[tier];
    } else {
      next[tier] = Number(value);
    }
    return next;
  };

  return (
    <div className="animate-fade-in">
      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setServiceSubTab('services')}
          className={`pb-2 px-4 font-medium text-sm ${serviceSubTab === 'services' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-500'}`}
        >
          Main Services
        </button>
        <button
          onClick={() => setServiceSubTab('addons')}
          className={`pb-2 px-4 font-medium text-sm ${serviceSubTab === 'addons' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-500'}`}
        >
          Add-ons
        </button>
      </div>

      {serviceSubTab === 'services' ? (
        <>
          {editingService && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto animate-scale-up">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-slate-800">
                    {editingService.id ? 'Edit Service' : 'Add New Service'}
                  </h3>
                  <button
                    onClick={() => setEditingService(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                      Service Name
                    </label>
                    <Input
                      className="border-2 border-slate-100 bg-white focus:border-teal-500 font-bold transition-all"
                      placeholder="e.g. Safety Critical Medical"
                      value={editingService.name}
                      onChange={(e) =>
                        setEditingService({ ...editingService, name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                      Description
                    </label>
                    <TextArea
                      className="border-2 border-slate-100 bg-white focus:border-teal-500 transition-all"
                      rows={3}
                      placeholder="Describe the service details..."
                      value={editingService.description}
                      onChange={(e) =>
                        setEditingService({ ...editingService, description: e.target.value })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                        Price (£)
                      </label>
                      <Input
                        type="number"
                        className="border-2 border-slate-100 bg-white focus:border-teal-500 font-bold"
                        value={editingService.price}
                        onChange={(e) =>
                          setEditingService({ ...editingService, price: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                        Duration (min)
                      </label>
                      <Input
                        type="number"
                        className="border-2 border-slate-100 bg-white focus:border-teal-500 font-bold"
                        value={editingService.durationMinutes}
                        onChange={(e) =>
                          setEditingService({
                            ...editingService,
                            durationMinutes: Number(e.target.value)
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Questionnaire Configuration Section */}
                  <div className="bg-teal-50/50 p-4 rounded-xl border border-teal-100 mt-2">
                    <p className="text-xs font-black text-teal-800 mb-3 uppercase tracking-widest flex items-center gap-2">
                      <FileText className="w-4 h-4 text-teal-600" /> Clinical Form Required?
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-[10px] font-bold text-teal-600 uppercase mb-1">
                          Select Form Template
                        </label>
                        <Select
                          className="border-2 border-white bg-white shadow-sm focus:border-teal-400 transition-all font-medium"
                          value={editingService.questionnaireConfig?.templateId || ''}
                          onChange={(e) => handleUpdateQuestionnaire(e.target.value)}
                          disabled={isLoadingQuestionnaires}
                        >
                          <option value="">
                            {isLoadingQuestionnaires
                              ? 'Loading templates...'
                              : 'No questionnaire required'}
                          </option>
                          {questionnaireOptions.map((q) => (
                            <option key={q.id} value={q.id}>
                              {q.name}
                            </option>
                          ))}
                        </Select>
                      </div>

                      {editingService.questionnaireConfig && (
                        <div className="grid grid-cols-2 gap-3 animate-fade-in">
                          <div>
                            <label className="block text-[10px] font-bold text-teal-600 uppercase mb-1">
                              Send Reminder
                            </label>
                            <Input
                              type="number"
                              className="border-2 border-white bg-white focus:border-teal-400"
                              value={editingService.questionnaireConfig.sendTimeValue}
                              onChange={(e) =>
                                setEditingService({
                                  ...editingService,
                                  questionnaireConfig: {
                                    ...editingService.questionnaireConfig!,
                                    sendTimeValue: Number(e.target.value)
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-teal-600 uppercase mb-1">
                              Timing Unit
                            </label>
                            <Select
                              className="border-2 border-white bg-white focus:border-teal-400"
                              value={editingService.questionnaireConfig.sendTimeUnit}
                              onChange={(e) =>
                                setEditingService({
                                  ...editingService,
                                  questionnaireConfig: {
                                    ...editingService.questionnaireConfig!,
                                    sendTimeUnit: isQuestionnaireSendTimeUnit(e.target.value)
                                      ? e.target.value
                                      : 'hours'
                                  }
                                })
                              }
                            >
                              {QUESTIONNAIRE_TIME_UNITS.map((unit) => (
                                <option key={unit.value} value={unit.value}>
                                  {unit.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mt-2">
                    <p className="text-xs font-bold text-slate-700 mb-3 uppercase tracking-widest">
                      Corporate Pricing Adjustments
                    </p>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="block text-[10px] font-bold text-orange-600 uppercase mb-1">
                          Bronze
                        </label>
                        <Input
                          type="number"
                          className="border bg-white font-bold"
                          placeholder="£"
                          value={editingService.tierPrices?.bronze ?? ''}
                          onChange={(e) =>
                            setEditingService({
                              ...editingService,
                              tierPrices: applyTierPrice(
                                editingService.tierPrices,
                                'bronze',
                                e.target.value
                              )
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                          Silver
                        </label>
                        <Input
                          type="number"
                          className="border bg-white font-bold"
                          placeholder="£"
                          value={editingService.tierPrices?.silver ?? ''}
                          onChange={(e) =>
                            setEditingService({
                              ...editingService,
                              tierPrices: applyTierPrice(
                                editingService.tierPrices,
                                'silver',
                                e.target.value
                              )
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-yellow-600 uppercase mb-1">
                          Gold
                        </label>
                        <Input
                          type="number"
                          className="border bg-white font-bold"
                          placeholder="£"
                          value={editingService.tierPrices?.gold ?? ''}
                          onChange={(e) =>
                            setEditingService({
                              ...editingService,
                              tierPrices: applyTierPrice(
                                editingService.tierPrices,
                                'gold',
                                e.target.value
                              )
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-bold text-indigo-600 uppercase mb-1">
                          Platinum
                        </label>
                        <Input
                          type="number"
                          className="border bg-white font-bold"
                          placeholder="£"
                          value={editingService.tierPrices?.platinum ?? ''}
                          onChange={(e) =>
                            setEditingService({
                              ...editingService,
                              tierPrices: applyTierPrice(
                                editingService.tierPrices,
                                'platinum',
                                e.target.value
                              )
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-cyan-600 uppercase mb-1">
                          Diamond
                        </label>
                        <Input
                          type="number"
                          className="border bg-white font-bold"
                          placeholder="£"
                          value={editingService.tierPrices?.diamond ?? ''}
                          onChange={(e) =>
                            setEditingService({
                              ...editingService,
                              tierPrices: applyTierPrice(
                                editingService.tierPrices,
                                'diamond',
                                e.target.value
                              )
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-8 pt-4 border-t border-slate-100">
                  <button
                    onClick={() => setEditingService(null)}
                    className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!editingService.name) return showAlertModal('Service name is required.');
                      if (editingService.id) {
                        await Promise.resolve(onUpdateService(editingService));
                      } else {
                        await Promise.resolve(
                          onAddService({
                            ...editingService,
                            id: createId()
                          })
                        );
                      }
                      setEditingService(null);
                      await refreshServices();
                    }}
                    className="bg-teal-600 text-white px-8 py-2.5 rounded-xl font-bold hover:bg-teal-700 shadow-lg shadow-teal-600/20 active:scale-95 transition-all"
                  >
                    Save Service
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-slate-800">Services Configuration</h3>
            <div className="flex items-center gap-3">
              <Input
                className="w-56"
                placeholder="Search services..."
                value={servicesSearch}
                onChange={(e) => setServicesSearch(e.target.value)}
              />
              {canEdit && (
                <button
                  onClick={() =>
                    setEditingService({
                      id: '',
                      name: '',
                      description: '',
                      price: 0,
                      durationMinutes: 0,
                      category: 'Medical',
                      tierPrices: {}
                    })
                  }
                  className="bg-slate-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-slate-800 transition-all shadow-sm"
                >
                  <Plus className="w-4 h-4" /> Add Service
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoadingServices ? (
              <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                Loading services...
              </div>
            ) : servicesError ? (
              <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-red-500">
                {servicesError}
              </div>
            ) : services.length === 0 ? (
              <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500 italic">
                No services found.
              </div>
            ) : (
              services.map((service) => {
                const linkedQ = questionnaireOptions.find(
                  (q) => q.id === service.questionnaireConfig?.templateId
                );
                return (
                  <div
                    key={service.id}
                    className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative group flex flex-col justify-between hover:shadow-xl transition-all"
                  >
                    <div>
                      {canEdit && (
                        <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setEditingService(service)}
                            className="p-2 bg-white/90 rounded-full border border-slate-100 shadow-sm text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              await Promise.resolve(onDeleteService(service.id));
                              await refreshServices();
                            }}
                            className="p-2 bg-white/90 rounded-full border border-slate-100 shadow-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      <h4 className="font-bold text-lg text-slate-900 mb-1">{service.name}</h4>
                      <p className="text-slate-500 text-sm mb-6 h-10 line-clamp-2 leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                    <div>
                      {linkedQ ? (
                        <div className="mb-4 bg-teal-50 border border-teal-100 p-2.5 rounded-xl flex items-start gap-3">
                          <div className="bg-teal-600 p-1.5 rounded-lg text-white">
                            <FileText className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <div className="text-[10px] uppercase font-black text-teal-700 tracking-widest mb-0.5">
                              Form Assigned
                            </div>
                            <div className="text-[11px] font-bold text-teal-900 leading-tight">
                              {linkedQ.name}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-4 bg-slate-50 border border-slate-100 p-2.5 rounded-xl flex items-start gap-3 opacity-60">
                          <div className="bg-slate-300 p-1.5 rounded-lg text-white">
                            <FileText className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <div className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-0.5">
                              No Form Required
                            </div>
                            <div className="text-[11px] font-bold text-slate-500 leading-tight italic">
                              Direct assessment only
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-2 pt-4 border-t border-slate-50">
                        <span className="font-black text-slate-900 text-lg">£{service.price}</span>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100 px-2.5 py-1 rounded-full">
                          {service.durationMinutes} MINS
                        </span>
                      </div>

                      {service.tierPrices &&
                        Object.values(service.tierPrices).some((v) => v != null) && (
                          <div className="mt-4 flex flex-wrap gap-2">
                            {service.tierPrices.bronze != null && (
                              <span className="bg-orange-50 text-orange-700 border border-orange-100 px-2 py-0.5 rounded text-[10px] font-bold">
                                B: £{service.tierPrices.bronze}
                              </span>
                            )}
                            {service.tierPrices.silver != null && (
                              <span className="bg-slate-50 text-slate-600 border border-slate-100 px-2 py-0.5 rounded text-[10px] font-bold">
                                S: £{service.tierPrices.silver}
                              </span>
                            )}
                            {service.tierPrices.gold != null && (
                              <span className="bg-yellow-50 text-yellow-700 border border-yellow-100 px-2 py-0.5 rounded text-[10px] font-bold">
                                G: £{service.tierPrices.gold}
                              </span>
                            )}
                            {service.tierPrices.platinum != null && (
                              <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded text-[10px] font-bold">
                                P: £{service.tierPrices.platinum}
                              </span>
                            )}
                            {service.tierPrices.diamond != null && (
                              <span className="bg-cyan-50 text-cyan-700 border border-cyan-100 px-2 py-0.5 rounded text-[10px] font-bold">
                                D: £{service.tierPrices.diamond}
                              </span>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <Pagination
            page={servicesPage}
            pageSize={servicesPageSize}
            totalCount={servicesTotalCount}
            onPageChange={setServicesPage}
            onPageSizeChange={(size) => {
              setServicesPageSize(size);
              setServicesPage(1);
            }}
            pageSizeOptions={[25, 50, 100]}
            isLoading={isLoadingServices}
          />
        </>
      ) : (
        <>
          {editingAddOn && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto animate-scale-up">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-slate-800">
                    {editingAddOn.id ? 'Edit Add-on' : 'Add New Add-on'}
                  </h3>
                  <button
                    onClick={() => setEditingAddOn(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                      Name
                    </label>
                    <Input
                      className="border-2 border-slate-100 bg-white focus:border-teal-500 font-bold"
                      placeholder="e.g. Audiometry"
                      value={editingAddOn.name}
                      onChange={(e) => setEditingAddOn({ ...editingAddOn, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                      Description
                    </label>
                    <TextArea
                      className="border-2 border-slate-100 bg-white focus:border-teal-500"
                      placeholder="Brief details about the add-on..."
                      value={editingAddOn.description}
                      onChange={(e) =>
                        setEditingAddOn({ ...editingAddOn, description: e.target.value })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                        Standard Price (£)
                      </label>
                      <Input
                        type="number"
                        className="border-2 border-slate-100 bg-white font-bold"
                        value={editingAddOn.price}
                        onChange={(e) =>
                          setEditingAddOn({ ...editingAddOn, price: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                        Duration (min)
                      </label>
                      <Input
                        type="number"
                        className="border-2 border-slate-100 bg-white font-bold"
                        value={editingAddOn.durationMinutes}
                        onChange={(e) =>
                          setEditingAddOn({
                            ...editingAddOn,
                            durationMinutes: Number(e.target.value)
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Questionnaire Configuration Section */}
                  <div className="bg-teal-50/50 p-4 rounded-xl border border-teal-100 mt-2">
                    <p className="text-xs font-black text-teal-800 mb-3 uppercase tracking-widest flex items-center gap-2">
                      <FileText className="w-4 h-4 text-teal-600" /> Clinical Form Required?
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-[10px] font-bold text-teal-600 uppercase mb-1">
                          Select Form Template
                        </label>
                        <Select
                          className="border-2 border-white bg-white shadow-sm focus:border-teal-400 transition-all font-medium"
                          value={editingAddOn.questionnaireConfig?.templateId || ''}
                          onChange={(e) => handleUpdateAddOnQuestionnaire(e.target.value)}
                          disabled={isLoadingQuestionnaires}
                        >
                          <option value="">
                            {isLoadingQuestionnaires
                              ? 'Loading templates...'
                              : 'No questionnaire required'}
                          </option>
                          {questionnaireOptions.map((q) => (
                            <option key={q.id} value={q.id}>
                              {q.name}
                            </option>
                          ))}
                        </Select>
                      </div>

                      {editingAddOn.questionnaireConfig && (
                        <div className="grid grid-cols-2 gap-3 animate-fade-in">
                          <div>
                            <label className="block text-[10px] font-bold text-teal-600 uppercase mb-1">
                              Send Reminder
                            </label>
                            <Input
                              type="number"
                              className="border-2 border-white bg-white focus:border-teal-400"
                              value={editingAddOn.questionnaireConfig.sendTimeValue}
                              onChange={(e) =>
                                setEditingAddOn({
                                  ...editingAddOn,
                                  questionnaireConfig: {
                                    ...editingAddOn.questionnaireConfig!,
                                    sendTimeValue: Number(e.target.value)
                                  }
                                })
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-teal-600 uppercase mb-1">
                              Timing Unit
                            </label>
                            <Select
                              className="border-2 border-white bg-white focus:border-teal-400"
                              value={editingAddOn.questionnaireConfig.sendTimeUnit}
                              onChange={(e) =>
                                setEditingAddOn({
                                  ...editingAddOn,
                                  questionnaireConfig: {
                                    ...editingAddOn.questionnaireConfig!,
                                    sendTimeUnit: isQuestionnaireSendTimeUnit(e.target.value)
                                      ? e.target.value
                                      : 'hours'
                                  }
                                })
                              }
                            >
                              {QUESTIONNAIRE_TIME_UNITS.map((unit) => (
                                <option key={unit.value} value={unit.value}>
                                  {unit.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mt-2">
                    <p className="text-xs font-bold text-slate-700 mb-3 uppercase tracking-widest">
                      Corporate Tier Pricing
                    </p>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div>
                        <label className="block text-[10px] font-bold text-orange-600 uppercase mb-1">
                          Bronze
                        </label>
                        <Input
                          type="number"
                          className="border bg-white font-bold"
                          value={editingAddOn.tierPrices?.bronze ?? ''}
                          onChange={(e) =>
                            setEditingAddOn({
                              ...editingAddOn,
                              tierPrices: applyTierPrice(
                                editingAddOn.tierPrices,
                                'bronze',
                                e.target.value
                              )
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                          Silver
                        </label>
                        <Input
                          type="number"
                          className="border bg-white font-bold"
                          value={editingAddOn.tierPrices?.silver ?? ''}
                          onChange={(e) =>
                            setEditingAddOn({
                              ...editingAddOn,
                              tierPrices: applyTierPrice(
                                editingAddOn.tierPrices,
                                'silver',
                                e.target.value
                              )
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-yellow-600 uppercase mb-1">
                          Gold
                        </label>
                        <Input
                          type="number"
                          className="border bg-white font-bold"
                          value={editingAddOn.tierPrices?.gold ?? ''}
                          onChange={(e) =>
                            setEditingAddOn({
                              ...editingAddOn,
                              tierPrices: applyTierPrice(
                                editingAddOn.tierPrices,
                                'gold',
                                e.target.value
                              )
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-bold text-indigo-600 uppercase mb-1">
                          Platinum
                        </label>
                        <Input
                          type="number"
                          className="border bg-white font-bold"
                          value={editingAddOn.tierPrices?.platinum ?? ''}
                          onChange={(e) =>
                            setEditingAddOn({
                              ...editingAddOn,
                              tierPrices: applyTierPrice(
                                editingAddOn.tierPrices,
                                'platinum',
                                e.target.value
                              )
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-cyan-600 uppercase mb-1">
                          Diamond
                        </label>
                        <Input
                          type="number"
                          className="border bg-white font-bold"
                          value={editingAddOn.tierPrices?.diamond ?? ''}
                          onChange={(e) =>
                            setEditingAddOn({
                              ...editingAddOn,
                              tierPrices: applyTierPrice(
                                editingAddOn.tierPrices,
                                'diamond',
                                e.target.value
                              )
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mt-2">
                    <p className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-widest">
                      Service Compatibility
                    </p>
                    <p className="text-[10px] text-slate-500 mb-3">
                      Check the services this add-on can be attached to during booking.
                    </p>
                    <div className="space-y-1 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                      {isLoadingServiceOptions ? (
                        <div className="text-xs text-slate-400 p-2">Loading services...</div>
                      ) : serviceOptions.length === 0 ? (
                        <div className="text-xs text-slate-400 p-2">No services found.</div>
                      ) : (
                        serviceOptions.map((s) => (
                          <div
                            key={s.id}
                            onClick={() => toggleServiceCompatibility(s.id)}
                            className="flex items-center gap-3 cursor-pointer hover:bg-white rounded-lg p-2 transition-all group border border-transparent hover:border-slate-100"
                          >
                            {editingAddOn.compatibleServiceIds?.includes(s.id) ? (
                              <CheckSquare className="w-5 h-5 text-teal-600" />
                            ) : (
                              <Square className="w-5 h-5 text-slate-300" />
                            )}
                            <span className="text-sm text-slate-700 font-medium group-hover:text-teal-700">
                              {s.name}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-8 pt-4 border-t border-slate-100">
                  <button
                    onClick={() => setEditingAddOn(null)}
                    className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!editingAddOn.name) return showAlertModal('Name is required.');
                      if (editingAddOn.id) {
                        await Promise.resolve(onUpdateAddOn(editingAddOn));
                      } else {
                        await Promise.resolve(
                          onAddAddOn({
                            ...editingAddOn,
                            id: createId(),
                            compatibleServiceIds: editingAddOn.compatibleServiceIds || []
                          })
                        );
                      }
                      setEditingAddOn(null);
                      await refreshAddOns();
                    }}
                    className="bg-teal-600 text-white px-8 py-2.5 rounded-xl font-bold hover:bg-teal-700 shadow-lg shadow-teal-600/20 active:scale-95 transition-all"
                  >
                    Save Add-on
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-slate-800">Service Add-ons</h3>
            <div className="flex items-center gap-3">
              <Input
                className="w-56"
                placeholder="Search add-ons..."
                value={addOnsSearch}
                onChange={(e) => setAddOnsSearch(e.target.value)}
              />
              {canEdit && (
                <button
                  onClick={() =>
                    setEditingAddOn({
                      id: '',
                      name: '',
                      description: '',
                      price: 0,
                      durationMinutes: 15,
                      compatibleServiceIds: [],
                      tierPrices: {},
                      questionnaireConfig: undefined
                    })
                  }
                  className="bg-slate-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-slate-800 transition-all shadow-sm"
                >
                  <Plus className="w-4 h-4" /> Add Add-on
                </button>
              )}
            </div>
          </div>
          <div className="space-y-4">
            {isLoadingAddOns ? (
              <div className="bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                Loading add-ons...
              </div>
            ) : addOnsError ? (
              <div className="bg-white p-6 rounded-xl border border-slate-200 text-center text-red-500">
                {addOnsError}
              </div>
            ) : addOns.length === 0 ? (
              <div className="bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500 italic">
                No add-ons found.
              </div>
            ) : (
              addOns.map((addon) => {
                const linkedQ = questionnaireOptions.find(
                  (q) => q.id === addon.questionnaireConfig?.templateId
                );
                return (
                  <div
                    key={addon.id}
                    className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex justify-between items-center hover:shadow-md transition-all"
                  >
                    <div className="flex-1 pr-8">
                      <h4 className="font-bold text-slate-900 text-lg leading-tight">
                        {addon.name}
                      </h4>
                      <p className="text-sm text-slate-500 mt-1 max-w-2xl">{addon.description}</p>
                      {linkedQ && (
                        <div className="mt-3 bg-teal-50 border border-teal-100 p-2.5 rounded-xl flex items-start gap-3 w-fit">
                          <div className="bg-teal-600 p-1.5 rounded-lg text-white">
                            <FileText className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <div className="text-[10px] uppercase font-black text-teal-700 tracking-widest mb-0.5">
                              Form Assigned
                            </div>
                            <div className="text-[11px] font-bold text-teal-900 leading-tight">
                              {linkedQ.name}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest">
                          Est. {addon.durationMinutes} MINS
                        </span>
                        <span className="bg-teal-50 text-teal-700 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest border border-teal-100">
                          {addon.compatibleServiceIds?.length || 0} COMPATIBLE SERVICES
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3 min-w-fit">
                      <div className="flex items-center gap-4">
                        <span className="font-black text-slate-900 text-xl">£{addon.price}</span>
                        {canEdit && (
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => setEditingAddOn(addon)}
                              className="p-2 bg-slate-50 rounded-lg text-blue-600 hover:bg-blue-50 border border-slate-100 transition-all"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={async () => {
                                await Promise.resolve(onDeleteAddOn(addon.id));
                                await refreshAddOns();
                              }}
                              className="p-2 bg-slate-50 rounded-lg text-red-600 hover:bg-red-50 border border-slate-100 transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      {addon.tierPrices &&
                        Object.values(addon.tierPrices).some((v) => v != null) && (
                          <div className="flex flex-wrap justify-end gap-2">
                            {addon.tierPrices.bronze != null && (
                              <span className="text-[9px] font-black uppercase bg-orange-50 text-orange-700 border border-orange-100 px-2 py-0.5 rounded-full">
                                B: £{addon.tierPrices.bronze}
                              </span>
                            )}
                            {addon.tierPrices.silver != null && (
                              <span className="text-[9px] font-black uppercase bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full">
                                S: £{addon.tierPrices.silver}
                              </span>
                            )}
                            {addon.tierPrices.gold != null && (
                              <span className="text-[9px] font-black uppercase bg-yellow-50 text-yellow-700 border border-yellow-100 px-2 py-0.5 rounded-full">
                                G: £{addon.tierPrices.gold}
                              </span>
                            )}
                            {addon.tierPrices.platinum != null && (
                              <span className="text-[9px] font-black uppercase bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded-full">
                                P: £{addon.tierPrices.platinum}
                              </span>
                            )}
                            {addon.tierPrices.diamond != null && (
                              <span className="text-[9px] font-black uppercase bg-cyan-50 text-cyan-700 border border-cyan-100 px-2 py-0.5 rounded-full">
                                D: £{addon.tierPrices.diamond}
                              </span>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <Pagination
            page={addOnsPage}
            pageSize={addOnsPageSize}
            totalCount={addOnsTotalCount}
            onPageChange={setAddOnsPage}
            onPageSizeChange={(size) => {
              setAddOnsPageSize(size);
              setAddOnsPage(1);
            }}
            pageSizeOptions={[25, 50, 100]}
            isLoading={isLoadingAddOns}
          />
        </>
      )}
    </div>
  );
};
